import { ampli } from '@/ampli';

import { fontStyles } from '@/styles/typography';

import { InfoIconOutline } from '@/ui/Icons/InfoIconOutline';
import {
  MatchaTooltip,
  MatchaTooltipContent,
  MatchaTooltipTrigger,
  MatchaTooltipPopover,
  MatchaTooltipBottomSheet,
} from '@/ui/MatchaTooltip';

import { TokenResult } from '@/utils/0x/token-registry.types';

import {
  SECURITY_LEVEL_TO_BADGE_VARIANT,
  getSecurityLabel,
  useSecurityAssessment,
} from '../../../hooks/useSecurityAssessment';
import { SecurityScoreBadge } from '../../SecurityScoreBadge';
import { ScoreContentLoader } from './ContentLoader';
import { BadgeIcon } from './RiskIcons/BadgeIcon';
import {
  SECURITY_LEVEL_GRADIENT,
  securityLevelContainerBg,
  securityLevelTextColor,
} from './styles';

interface SecurityScoreProps {
  /** Token to display security score for */
  activeToken: TokenResult;
  isLoading: boolean;
}

export const SecurityAuditPreview = ({ activeToken, isLoading }: SecurityScoreProps) => {
  if (isLoading) {
    return (
      <span className={fontStyles({ variant: 'LABEL_SM', class: ['ml-1', 'text-inherit'] })}>
        <ScoreContentLoader />
      </span>
    );
  }

  return <SecurityAuditPreviewContent activeToken={activeToken} />;
};

interface ContentProps {
  activeToken: TokenResult;
}

const SecurityAuditPreviewContent = ({ activeToken }: ContentProps) => {
  const { level, risks, highRisks } = useSecurityAssessment({
    activeToken,
  });

  const totalRisks = risks + highRisks;
  const securityLabel = getSecurityLabel(level, totalRisks);
  const badgeVariant = SECURITY_LEVEL_TO_BADGE_VARIANT[level];
  const gradient = SECURITY_LEVEL_GRADIENT[level];

  const handleScoreClick = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('securityAudit')?.scrollIntoView({ behavior: 'smooth' });

    ampli.securityAuditClicked({
      token_address: activeToken.address,
      token_chain_id: activeToken.chainId,
      token_chain_name: activeToken.chainName,
      token_symbol: activeToken.symbol,
    });
  };

  return (
    <MatchaTooltip>
      <div className="mr-1">
        <MatchaTooltipTrigger>
          <div
            className={fontStyles({
              variant: 'LABEL_XS',
              class: [
                'text-token-gray-800',
                'hidden',
                'tablet-desktop-links:flex',
                'min-w-fit',
                'font-normal',
                'ml-1',
                'items-center',
                'mb-[7px]',
              ],
            })}
          >
            Security audit
            <InfoIconOutline className="text-token-gray-700 cursor-default h-3 w-3 ml-1 hover:cursor-default" />
          </div>
        </MatchaTooltipTrigger>
        <SecurityScoreBadge
          level={level}
          numberOfRisks={totalRisks}
          onClick={handleScoreClick}
          className="cursor-pointer"
        />
      </div>
      <MatchaTooltipContent>
        <div className="block max-w-[224px]">
          <h2
            className={fontStyles({
              variant: 'LABEL_SM',
              class: ['mb-2', 'hidden', 'tablet-desktop-links:flex', 'text-token-gray-1600'],
            })}
          >
            Security Audit
          </h2>
          <div className="flex flex-col">
            <div
              className={securityLevelContainerBg({
                level,
                class: [
                  'flex',
                  'flex-col',
                  'items-center',
                  'justify-center',
                  'rounded-[16px]',
                  'relative',
                  'overflow-hidden',
                  'border-solid',
                  'min-h-[106px]',
                  'border-primitive-basic-black/6',
                  'border',
                ],
              })}
            >
              <div
                className="rotate-[-16.841deg] absolute top-[-25%] left-0 w-full h-full overflow-hidden pointer-events-none x-0"
                style={gradient}
              />
              <BadgeIcon size={32} variant={badgeVariant} />
              <p
                className={fontStyles({
                  variant: 'LABEL_XS',
                  class: ['mt-2 flex justify-center', securityLevelTextColor({ level })],
                })}
              >
                {securityLabel}
              </p>
            </div>
            <span
              className={fontStyles({ variant: 'BODY_SM', class: ['mt-2', 'text-token-gray-800'] })}
            >
              Audits can miss issues. Results are not verified by Matcha. Always conduct your own
              research.
            </span>
          </div>
        </div>
      </MatchaTooltipContent>
      <MatchaTooltipPopover align="start" className="py-3 block" side="right" sideOffset={8} />
      <MatchaTooltipBottomSheet header="Token Risks" />
    </MatchaTooltip>
  );
};
